import { Controller } from "@hotwired/stimulus"

// The weekly report's flight-style range picker (plan section 4): one month
// grid, first click sets the start endpoint, hover previews the span,
// second click sets the end and applies (plain GET, turbo off); clicking on
// or before the start restarts the selection, exactly like a flight site.
// Future days disabled; Esc closes. No dependency: ~150 lines of vanilla
// Stimulus, department-prefixed identifier per the fleet convention.
export default class extends Controller {
  static targets = ["popover", "chip"]
  static values = { url: String, start: String, end: String }

  connect() {
    this.start = this.startValue ? new Date(this.startValue + "T00:00:00") : null
    this.end = this.endValue ? new Date(this.endValue + "T00:00:00") : null
    this.hover = null
    const base = this.start || new Date()
    this.viewYear = base.getFullYear()
    this.viewMonth = base.getMonth()
    this.boundAway = this.clickAway.bind(this)
    this.boundKey = this.keydown.bind(this)
  }

  disconnect() {
    document.removeEventListener("click", this.boundAway)
    document.removeEventListener("keydown", this.boundKey)
  }

  toggle(event) {
    event.stopPropagation()
    const open = this.popoverTarget.style.display !== "none"
    open ? this.close() : this.open()
  }

  open() {
    this.selecting = false
    this.applying = false
    this.render()
    // FIXED positioning from the chip's viewport rect (owner bug report:
    // absolute positioning clipped the popover behind the card borders).
    const rect = this.chipTarget.getBoundingClientRect()
    const top = Math.min(rect.bottom + 6, window.innerHeight - 380)
    const left = Math.min(rect.left, window.innerWidth - 300)
    this.popoverTarget.style.cssText = `display:block;position:fixed;top:${Math.max(top, 8)}px;left:${Math.max(left, 8)}px;z-index:9999;`
    document.addEventListener("click", this.boundAway)
    document.addEventListener("keydown", this.boundKey)
  }

  close() {
    this.popoverTarget.style.display = "none"
    document.removeEventListener("click", this.boundAway)
    document.removeEventListener("keydown", this.boundKey)
  }

  clickAway(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  keydown(event) {
    if (event.key === "Escape") this.close()
  }

  prevMonth(event) { event.stopPropagation(); this.shiftMonth(-1) }
  nextMonth(event) { event.stopPropagation(); this.shiftMonth(1) }

  shiftMonth(delta) {
    this.viewMonth += delta
    if (this.viewMonth < 0) { this.viewMonth = 11; this.viewYear-- }
    if (this.viewMonth > 11) { this.viewMonth = 0; this.viewYear++ }
    this.render()
  }

  pick(event) {
    event.stopPropagation()
    // A range is already applying (committee 2026-09-09, Seat A): the
    // second click of a Mac double-click on the end day used to land here
    // with selecting=false, restart the range, and leave apply() with no
    // end to navigate to. Ignore clicks until the navigation starts.
    if (this.applying) return
    const iso = event.currentTarget.dataset.date
    if (!iso) return
    const day = new Date(iso + "T00:00:00")
    if (day > this.today()) return

    if (!this.selecting || !this.start || day < this.start) {
      // First click, or a click before the start: (re)start the range.
      // Clicking the start day again completes a one-day range instead.
      this.start = day
      this.end = null
      this.hover = null
      this.hoverIso = null
      this.selecting = true
      this.render()
      return
    }

    this.end = day
    this.selecting = false
    this.applying = true
    this.render()
    setTimeout(() => this.apply(), 250)
  }

  // Hover only REPAINTS the existing cells; it never rebuilds the grid.
  // Owner bug 2026-09-09: rebuilding innerHTML on every mouseover replaced
  // the button under the pointer between mousedown and mouseup, so the end
  // day could never be clicked (Chrome re-fires mouseover on the new node,
  // which rebuilt again, every frame). The start click already survives
  // because nothing is hovered yet when it lands.
  preview(event) {
    if (!this.selecting || !this.start) return
    const iso = event.currentTarget.dataset.date
    if (iso === this.hoverIso) return
    this.hoverIso = iso
    this.hover = iso ? new Date(iso + "T00:00:00") : null
    this.paint()
  }

  apply() {
    const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    window.location = `${this.urlValue}?start_date=${fmt(this.start)}&end_date=${fmt(this.end)}`
  }

  today() {
    const t = new Date(); t.setHours(0, 0, 0, 0); return t
  }

  // One cell's presentation from the current selection state, shared by the
  // build (render) and the in-place repaint (paint) so the two never drift.
  cellState(date) {
    const spanEnd = this.end || (this.selecting ? this.hover : null)
    const future = date > this.today()
    const isStart = !!this.start && date.getTime() === this.start.getTime()
    const isEnd = !!this.end && date.getTime() === this.end.getTime()
    const inSpan = !!(this.start && spanEnd && date > this.start && date < spanEnd)
    let style = "width:34px;height:30px;display:inline-flex;align-items:center;justify-content:center;font-size:12px;border-radius:6px;border:none;background-clip:padding-box;"
    if (future) {
      style += "color:#c8c2ba;cursor:default;background:transparent;"
    } else if (isStart || isEnd) {
      style += "background:#2D2D2D;color:#ffffff;cursor:pointer;"
    } else if (inSpan) {
      style += "background:#F5F3F0;color:#2D2D2D;cursor:pointer;"
    } else {
      style += "color:#2D2D2D;cursor:pointer;background:transparent;"
    }
    return { future, selected: isStart || isEnd || inSpan, style }
  }

  paint() {
    this.popoverTarget.querySelectorAll("button[data-date]").forEach(button => {
      const state = this.cellState(new Date(button.dataset.date + "T00:00:00"))
      button.style.cssText = state.style
      button.setAttribute("aria-selected", state.selected)
    })
    // No caption write here: it cannot change during a hover, and a DOM
    // text swap inside a tap's synthetic mouseover is what iPad Safari
    // treats as "content changed", which suppresses the click.
  }

  caption() {
    return this.selecting ? "Now pick the end date" : "Pick a start date"
  }

  render() {
    this.hoverIso = null
    const first = new Date(this.viewYear, this.viewMonth, 1)
    const label = first.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    // Monday-start grid (matches the ledger's week grouping).
    const lead = (first.getDay() + 6) % 7
    const daysIn = new Date(this.viewYear, this.viewMonth + 1, 0).getDate()

    let cells = ""
    for (let i = 0; i < lead; i++) cells += `<span style="width:34px;height:30px;"></span>`
    for (let d = 1; d <= daysIn; d++) {
      const date = new Date(this.viewYear, this.viewMonth, d)
      const iso = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      const state = this.cellState(date)
      const actions = state.future ? "" : `data-action="click->spt-range-calendar#pick mouseover->spt-range-calendar#preview" data-date="${iso}"`
      cells += `<button type="button" role="gridcell" aria-selected="${state.selected}" style="${state.style}" ${actions}>${d}</button>`
    }

    this.popoverTarget.innerHTML = `
      <div role="grid" style="background:var(--spt-bg-white,#fff);border:1px solid var(--spt-border,#E8E4DF);border-radius:12px;box-shadow:0 4px 12px rgba(0,0,0,0.12);padding:12px;width:280px;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
          <button type="button" class="spt-btn-ghost spt-btn-sm" data-action="click->spt-range-calendar#prevMonth">&larr;</button>
          <strong style="font-size:13px;">${label}</strong>
          <button type="button" class="spt-btn-ghost spt-btn-sm" data-action="click->spt-range-calendar#nextMonth">&rarr;</button>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:1px;font-size:11px;color:#8C8C8C;">
          ${["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map(d => `<span style="width:34px;text-align:center;">${d}</span>`).join("")}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:1px;">${cells}</div>
        <p data-range-caption style="font-size:11px;color:#8C8C8C;margin:8px 0 0;">${this.caption()}</p>
      </div>`
  }
}
